import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  FormField,
  PasswordInput,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { PortalShell } from "../components/PortalShell";
import { portalAuthApi } from "../api/auth";
import { PortalApiError } from "../api/client";

// External Portal v2 §3.2 — landing page for the v2 invite + v2
// migration sweep emails. Consumes the set-password token from the
// ?token=... query string, accepts a new password (twice), POSTs to
// /api/portal/auth/set-password/confirm, then directs the user to
// the sign-in page.
//
// Before rendering the form it validates the token (non-consuming): a
// stale / used / already-activated link is sent straight to sign-in
// without interaction, instead of letting the user fill the form only to
// be rejected on submit.

const MIN_LENGTH = 12;

export function PortalSetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [checking, setChecking] = useState(true);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { submit, pending } = useSubmittingMutation(
    () => portalAuthApi.setPasswordConfirm(token, pw),
    {
      onSuccess: () => setDone(true),
      onError: (err) => {
        setError(
          err instanceof PortalApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Couldn't set your password.",
        );
      },
    },
  );

  // Validate the token up front. Anything that can't activate an account
  // (missing / expired / consumed / already has a password) bounces to
  // sign-in before the form is ever shown.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!token) {
        if (!cancelled) navigate("/portal/login", { replace: true });
        return;
      }
      try {
        const status = await portalAuthApi.validateSetPasswordToken(token);
        if (cancelled) return;
        if (!status.valid) {
          navigate("/portal/login", { replace: true });
          return;
        }
        setChecking(false);
      } catch {
        if (!cancelled) navigate("/portal/login", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError("This link is missing the setup token. Check the email and try again.");
      return;
    }
    if (pw.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (pw !== pw2) {
      setError("Passwords don't match.");
      return;
    }
    void submit();
  }

  if (checking) {
    return (
      <PortalShell pageTitle="Set up your account">
        <p className="text-small text-gray-mid" data-testid="set-password-checking">
          Checking your link…
        </p>
      </PortalShell>
    );
  }

  if (done) {
    return (
      <PortalShell pageTitle="Password set">
        <h1 className="text-h2 font-display text-onyx m-0">You're all set</h1>
        <p className="text-small text-onyx mt-space-3">
          Your password is saved. You can sign in any time.
        </p>
        <p className="mt-space-4">
          <Link to="/portal/login" className="underline" data-testid="set-password-go-login">
            Go to sign-in
          </Link>
        </p>
      </PortalShell>
    );
  }

  return (
    <PortalShell pageTitle="Set up your account">
      <h1 className="text-h2 font-display text-onyx m-0">Set your password</h1>
      <p className="text-small text-gray-mid mt-space-2">
        Choose a password to finish setting up your Phoenix Capital portal account.
        After this you'll sign in with your email and this password.
      </p>

      <form
        onSubmit={handleSubmit}
        className="mt-space-5 flex flex-col gap-space-4"
        noValidate
        data-testid="set-password-form"
      >
        <FormField label={`New password (at least ${MIN_LENGTH} characters)`} required>
          <PasswordInput
            autoComplete="new-password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            disabled={pending}
            data-testid="set-password-pw"
          />
        </FormField>
        <FormField label="Confirm new password" required>
          <PasswordInput
            autoComplete="new-password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            disabled={pending}
            data-testid="set-password-pw2"
          />
        </FormField>

        {error && (
          <p className="text-small text-status-error m-0" role="alert">
            {error}
          </p>
        )}

        <SubmitButton type="submit" pending={pending} pendingLabel="Saving…" data-testid="set-password-submit">
          Save password
        </SubmitButton>
        <SubmittingHint pending={pending} />
      </form>

      <p className="text-caption text-gray-mid mt-space-4">
        Already set your password?{" "}
        <Link to="/portal/login" className="underline">Sign in</Link>
      </p>
    </PortalShell>
  );
}
